import { HistoricalUtrs } from '../../../../api/insights';
import Table, { TableColumn } from '../../../../components/table/Table';
import { GridDashboardTableItem } from '../../../../types/insight-custom-dashboard';
import { extractVariables, tryCalculation } from '../../../../utils/formula';
import { getActualUtrsData, getDisplayNumericValue } from './utils';
import { DATE, formatDateUTC, sortEffectiveDateDesc } from '../../../../utils/date';
import './TableWidget.scss';
import { getVariablesWithValues } from '../charts/multi-utrs-chart/utils';
import { truncate } from '../../../../utils/string';

const NUMBER_OF_COLS = 3;

type TableWidgetProps = Pick<GridDashboardTableItem, 'calculation' | 'variables' | 'config'> & {
  title?: string;
  utrsData: HistoricalUtrs[];
  onClickUtrValueLabel: (utrCode: string) => void;
};

type TableWidgetData = {
  name: string;
  utrCode: string;
  unitText: string;
  [k: string]: string | number;
};

type TableWidgetColumn = TableColumn<TableWidgetData> & {
  id: string; 
}

const getChartData = ({
  utrsData,
  variables,
  calculation,
  title,
  onClickUtrValueLabel,
  config: { hiddenColumns },
}: Required<TableWidgetProps>) => {
  const actualUtrsData = getActualUtrsData(utrsData);
  const mergedEffectiveDates = actualUtrsData.reduce((acc, utrData) => {
    const effectiveDates = utrData.utrvs
      .filter((utrv) => !acc.includes(utrv.effectiveDate))
      .map((utrv) => utrv.effectiveDate);
    return acc.concat(effectiveDates);
  }, [] as string[]);
  const latestEffectiveDates = mergedEffectiveDates.sort(sortEffectiveDateDesc).slice(0, NUMBER_OF_COLS);

  const columns: TableWidgetColumn[] = [
    {
      id: 'name',
      Header: () => <span className='text-capitalize'>{title}</span>,
      cellProps: {
        className: 'flex-grow-1',
      },
      Cell: ({ row }) => (
        <div
          className='text-left cell__value-label'
          color='link'
          onClick={() => onClickUtrValueLabel(row.original.utrCode)}
        >
          {truncate(row.original.name, 125)}
        </div>
      ),
      disableSortBy: true,
    },
    {
      id: 'unit',
      Header: () => 'Unit',
      cellProps: {
        className: 'flex-grow-0 text-center',
      },
      Cell: ({ row }) => row.original.unitText,
      disableSortBy: true,
    },
    ...latestEffectiveDates.map(
      (effectiveDate): TableWidgetColumn => ({
        id: effectiveDate,
        Header: () => formatDateUTC(effectiveDate, DATE.MONTH_YEAR_SHORT),
        cellProps: {
          className: 'flex-grow-0 text-center',
        },
        Cell: ({ row }) => row.original[effectiveDate] || '',
        disableSortBy: true,
      })
    ),
  ];

  const filteredColumns = hiddenColumns ? columns.filter((c) => !hiddenColumns.includes(c.id)) : columns;

  const data: TableWidgetData[] = calculation.values.map((value) => {
    const variableLetters = extractVariables(value.formula ?? '');
    const utrCode = variableLetters.length !== 1 ? '' : variables[variableLetters[0]].code;
    const utrData = actualUtrsData.find((data) => data.utr.code === utrCode);

    const dataByColumns = utrData
      ? latestEffectiveDates.reduce((acc, effectiveDate) => {
          const utrv = utrData.utrvs.find((utrv) => utrv.effectiveDate === effectiveDate);

          const variablesWithValues = getVariablesWithValues({
            utrsData: actualUtrsData,
            variables,
            utrvs: utrv ? [utrv] : [],
            fallback: '',
          });
          const calculationValue = tryCalculation({
            formula: value.formula || '',
            variables: variablesWithValues,
            fallback: '',
            infinityFallback: '',
          });
          acc[effectiveDate] = getDisplayNumericValue(calculationValue);
          return acc;
        }, {} as Record<string, string | number>)
      : {};

    return {
      name: value.name,
      utrCode,
      unitText: utrData?.utr.unit || '',
      ...dataByColumns,
    };
  });

  return {
    filteredColumns,
    data,
  };
};

export const TableWidget = (props: TableWidgetProps) => {
  const { utrsData, variables, calculation, title = '', onClickUtrValueLabel, config = {} } = props;

  if (!utrsData.length || !variables || !calculation) {
    return null;
  }

  const { filteredColumns, data } = getChartData({ utrsData, variables, calculation, title, onClickUtrValueLabel, config });

  return (
    <div className='w-100 h-100 table-widget__container'>
      <Table columns={filteredColumns} data={data} />
    </div>
  );
};
